import {
  DEFAULT_CURRENCY,
  formatMoney,
  isHttpUrl,
  outfitBudgetLine,
  outfitPieces,
  outfitStatus,
  priceDrop,
  summarize,
  type User,
} from '@closetly/core';
import { useState } from 'react';
import { useItemEditor } from '../lib/editor';
import { storedImageUrl } from '../lib/image';
import { useBoards, useItems, useMe, useOutfits } from '../lib/queries';
import { navigate } from '../lib/router';
import { Collage } from './Collage';
import { BookmarkIcon } from './Icons';
import { ShareSheet } from './ShareSheet';

/** One outfit as a shopping list: what is in it, what is still missing, where to buy it. */
export function OutfitDetail({ user, id }: { user: User; id: string }) {
  const outfits = useOutfits(user.id);
  const boards = useBoards(user.id);
  const items = useItems(user.id);
  const me = useMe(user);
  const editor = useItemEditor();
  const [sharing, setSharing] = useState(false);
  const currency = me.data?.currency || DEFAULT_CURRENCY;
  const fmt = (c: number) => formatMoney(c, currency);

  const outfit = outfits.data?.find((o) => o.id === id);
  if (!outfit) {
    return (
      <div className="empty">
        <strong>{outfits.isPending ? 'Lade…' : 'Outfit nicht gefunden'}</strong>
        <button type="button" className="btn" onClick={() => navigate('/outfits')}>
          Zu den Outfits
        </button>
      </div>
    );
  }

  const all = items.data ?? [];
  const pieces = outfitPieces(outfit, all);
  const status = outfitStatus(pieces);
  const budget = summarize(all, me.data?.budget_cents ?? 0, currency).budget;
  const budgetLine = outfitBudgetLine(status, budget ? budget.leftCents : null, currency);
  const onBoards = (boards.data ?? []).filter((b) => b.outfits.includes(outfit.id));

  return (
    <>
      <button type="button" className="back linkbtn" onClick={() => navigate('/outfits')}>
        ‹ Alle Outfits
      </button>
      <div className="detail">
        <div className="detail-visual">
          <Collage pieces={pieces} big />
        </div>

        <div className="detail-info">
          <h2>{outfit.name || 'Outfit ohne Namen'}</h2>
          {onBoards.length > 0 && (
            <div className="meta">
              {onBoards.map((b) => (
                <a
                  key={b.id}
                  href={`/boards/${b.id}`}
                  onClick={(e) => {
                    e.preventDefault();
                    navigate(`/boards/${b.id}`);
                  }}
                >
                  {b.name}
                </a>
              ))}
            </div>
          )}

          {status.count > 0 && (
            <section className="buy" aria-label="Einkauf">
              <div className="buy-progress">
                <span>
                  {status.complete ? 'Komplett' : `${status.bought} von ${status.count} gekauft`}
                </span>
                <div className="bar" role="img" aria-label={`${status.bought} von ${status.count} gekauft`}>
                  <i style={{ width: `${(status.bought / status.count) * 100}%` }} />
                </div>
              </div>
              <dl>
                <div>
                  <dt>Ganzes Outfit</dt>
                  <dd>{fmt(status.totalCents)}</dd>
                </div>
                {!status.complete && (
                  <div>
                    <dt>Noch zu kaufen</dt>
                    <dd>{fmt(status.missingCents)}</dd>
                  </div>
                )}
              </dl>
              {budgetLine && <p className="hint">{budgetLine}</p>}
            </section>
          )}

          {outfit.note && <p className="note">{outfit.note}</p>}

          <ul className="pieces">
            {pieces.map((p) => {
              const img = storedImageUrl(p);
              const drop = p.bought ? null : priceDrop(p);
              return (
                <li key={p.id} className={p.bought ? 'bought' : ''}>
                  <button type="button" className="piece" onClick={() => editor.openItem(p)} aria-label={`${p.name} bearbeiten`}>
                    {img ? <img src={img} alt="" loading="lazy" /> : <span className="piece-ph" aria-hidden="true" />}
                    <span className="piece-text">
                      <b>{p.name}</b>
                      <small>{[p.shop, p.size && `Gr. ${p.size}`].filter(Boolean).join(' · ')}</small>
                    </span>
                    <span className={`piece-price${drop ? ' drop' : ''}`}>
                      {p.price_cents ? fmt(p.price_cents) : '–'}
                      {drop && <s aria-label={`statt ${fmt(drop.beforeCents)}`}>{fmt(drop.beforeCents)}</s>}
                    </span>
                  </button>
                  <div className="piece-actions">
                    {p.link && isHttpUrl(p.link) && !p.bought && (
                      <a className="btn" href={p.link} target="_blank" rel="noopener noreferrer">
                        Zum Shop ↗
                      </a>
                    )}
                    <button
                      type="button"
                      className={`btn${p.bought ? '' : ' primary'}`}
                      disabled={p.pending}
                      onClick={() => editor.toggleBought(p)}
                    >
                      {p.bought ? 'Gekauft ✓' : 'Gekauft'}
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>

          <div className="detail-actions">
            <button
              type="button"
              className="btn icon-btn"
              onClick={() => editor.openSave({ type: 'outfit', id: outfit.id, name: outfit.name || 'Outfit' })}
            >
              <BookmarkIcon /> Merken
            </button>
            <button type="button" className="btn" onClick={() => setSharing(true)}>
              Teilen
            </button>
            <button type="button" className="btn" onClick={() => editor.openNew({ outfitId: outfit.id })}>
              + Teil per Link
            </button>
            <button type="button" className="btn ink" onClick={() => navigate(`/outfits/${outfit.id}/bearbeiten`)}>
              Bearbeiten
            </button>
          </div>
        </div>
      </div>
      {sharing && (
        <ShareSheet
          uid={user.id}
          target={{ kind: 'outfit', id: outfit.id, name: outfit.name }}
          onClose={() => setSharing(false)}
        />
      )}
    </>
  );
}
